import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ApiService from '../services/api';
import CompetitorMisspelledWords from './CompetitorMisspelledWords';

const CompetitorPractice = ({ onBack }) => {
  const { user } = useAuth();
  const [currentWord, setCurrentWord] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [bees, setBees] = useState([]);
  const [selectedBee, setSelectedBee] = useState(null);
  const [practiceStarted, setPracticeStarted] = useState(false);
  const [showMisspelledWords, setShowMisspelledWords] = useState(false);

  useEffect(() => {
    loadBees();
  }, []);

  const loadBees = async () => {
    try {
      setLoading(true);
      const response = await ApiService.getAllBees();
      if (response.success) {
        setBees(response.bees || []);
      } else {
        setError('Failed to load spelling bees');
      }
    } catch (err) {
      // Error loading bees
      setError('Failed to load spelling bees');
    } finally {
      setLoading(false);
    }
  };

  const startPractice = async () => {
    if (!selectedBee) {
      setError('Please select a spelling bee first');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Use competitor_id to get the word that would be shown to this competitor
      const response = await ApiService.getNextWordForCompetitor(user.competitor_id, selectedBee.bee_id);
      
      if (response.word) {
        setCurrentWord(response.word);
        setPracticeStarted(true);
      } else {
        setError(response.error || 'Failed to get word');
      }
    } catch (err) {
      // Error getting word
      setError('Failed to get word');
    } finally {
      setLoading(false);
    }
  };

  const getNextWord = async () => {
    if (!selectedBee) return;

    try {
      setLoading(true);
      setError(null);
      
      const response = await ApiService.getNextWordForCompetitor(user.competitor_id, selectedBee.bee_id);
      
      if (response.word) {
        setCurrentWord(response.word);
      } else {
        setError(response.error || 'Failed to get next word');
      }
    } catch (err) {
      // Error getting next word
      setError('Failed to get next word');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !practiceStarted) {
    return <div className="loading">Loading...</div>;
  }

  // Show misspelled words view if requested
  if (showMisspelledWords && selectedBee) {
    return (
      <div className="container">
        <CompetitorMisspelledWords
          competitor={user}
          selectedBee={selectedBee}
          onBack={() => setShowMisspelledWords(false)}
        />
      </div>
    );
  }

  if (!practiceStarted) {
    return (
      <div className="container">
        <div className="card">
          <button onClick={onBack} className="button secondary">
            ← Back
          </button>
          
          <h1 className="title">Competitor Practice</h1>
          <p className="subtitle">
            Welcome, {user.name}! Select a spelling bee to practice with.
          </p>
          
          {error && (
            <div className="error">
              {error}
            </div>
          )}

          <div className="bee-selector">
            <h3>Available Spelling Bees</h3>
            {bees.length === 0 ? (
              <p>No spelling bees available.</p>
            ) : (
              <div className="bee-list">
                {bees.map((bee) => (
                  <div
                    key={bee.bee_id}
                    className={`bee-item ${selectedBee?.bee_id === bee.bee_id ? 'selected' : ''}`}
                    onClick={() => setSelectedBee(bee)}
                  >
                    <div className="bee-name">{bee.name}</div>
                    <div className="bee-date">{bee.date}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {selectedBee && (
            <button
              onClick={startPractice}
              className="button"
              disabled={loading}
            >
              {loading ? 'Starting...' : 'Start Practice'}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card">
        <button onClick={onBack} className="button secondary">
          ← Back
        </button>
        
        <h1 className="title">Practice Mode</h1>
        <p className="subtitle">
          {selectedBee?.name} - Read-Only Practice
        </p>
        
        {error && (
          <div className="error">
            {error}
          </div>
        )}

        <div className="word-display">
          {currentWord || 'No word available'}
        </div>

        <div className="practice-info">
          <p style={{ color: '#718096', fontSize: '0.9rem', textAlign: 'center' }}>
            This is read-only practice mode. You can see the words but cannot record attempts.
            The words shown are the same sequence your coach would see for your practice.
          </p>
        </div>

        <div className="practice-controls">
          <button
            onClick={getNextWord}
            className="button"
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Next Word'}
          </button>

          <button
            onClick={() => setShowMisspelledWords(true)}
            className="button"
            style={{ background: '#fd7e14' }}
          >
            View Misspelled Words (48h)
          </button>

          <button
            onClick={() => {
              setPracticeStarted(false);
              setCurrentWord('');
              setSelectedBee(null);
            }}
            className="button secondary"
          >
            Change Spelling Bee
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompetitorPractice;